import { useMutation, useQuery } from "@tanstack/react-query";
import { fetchBlob } from "@/shared/api/http-client";
import { downloadBlob } from "@/shared/lib/download-file";
import { VENUE_DETAIL_QUERY_KEY } from "./constants";

/**
 * `GET /api/admin/venues/:id/qr/download` — the admin/support copy of the
 * owner's QR download. Same use case on the API, same PNG, same
 * `QR_BASE_URL/scan/:token` payload. 400 when the venue is not ACTIVE (no
 * token yet), 404 unknown id. Hand-written against `fetchBlob` (binary) —
 * the Orval client cannot return a Blob (CLAUDE.md → TD-07).
 */
function fetchVenueQrBlob(id: string): Promise<Blob> {
  return fetchBlob(`/api/admin/venues/${encodeURIComponent(id)}/qr/download`);
}

/** Mirrors the API's `Content-Disposition` attachment name. */
export function venueQrFilename(venueName: string): string {
  const safeName = venueName.replace(/[^a-zA-Z0-9-_]/g, "_");
  return `venue-${safeName}-qr.png`;
}

export interface DownloadVenueQrVariables {
  readonly id: string;
  readonly name: string;
}

/** Fetches the PNG and hands it to the browser as a file download. */
export function useDownloadVenueQr() {
  return useMutation({
    mutationFn: async ({ id, name }: DownloadVenueQrVariables) => {
      const blob = await fetchVenueQrBlob(id);
      downloadBlob(blob, venueQrFilename(name));
    },
  });
}

/** Blob → `data:<type>;base64,…` through the browser's own encoder. */
function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error ?? new Error("Could not read the QR image"));
    reader.readAsDataURL(blob);
  });
}

/**
 * The PNG as a `data:` URL for an `<img>` (print sheet).
 *
 * NOT a `blob:` object URL: the backoffice ships a strict CSP
 * (`firebase.json` → `img-src 'self' data: https:`) and `blob:` is not in
 * it, so an `<img src="blob:…">` is silently blocked — the download request
 * returns 200 and the image still renders broken (seen on dev 2026-09-30).
 * A base64 data URL is plain cacheable data with no lifecycle to manage
 * (nothing to revoke), and a 4 KB PNG costs ~5.5 KB encoded.
 */
export function useVenueQrDataUrl(id: string | undefined) {
  return useQuery({
    queryKey: [...VENUE_DETAIL_QUERY_KEY, id, "qr-data-url"] as const,
    queryFn: async () => {
      if (!id) throw new Error("useVenueQrDataUrl requires an id");
      return blobToDataUrl(await fetchVenueQrBlob(id));
    },
    enabled: Boolean(id),
    staleTime: 5 * 60_000,
    retry: false,
  });
}
